import React, { Dispatch, SetStateAction } from 'react';
import { Dropdown } from 'react-bootstrap';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { AccountingEntry } from '../../models/accounting.entry.model';
import { Company } from '../../models/company.model';
import { TreasuryAccount } from '../../models/treasury.account.model';
import { Invoice } from '../../models/invoice.model';
import { legendValid, legendValidIcon, legendValidInvalidIconRestart, legendValidInvalidRestart } from '../../tools/legend.data.entry.tool';
import { accountingEntryFormat } from '../../scripts/format.value.script';
import { prefixLegend } from '../../scripts/name.legend.script';

import AppLegend from './Legend';

export interface AppInputAccountingEntryProps {
  component?: string,
  solid: boolean,
  companyForUser: Company,
  accountingAccountsForCompany: AccountingEntry[],
  treasuryAccountsForCompany: TreasuryAccount[],
  invoice?: Invoice,
  accountingEntry: {value: string, valid: boolean, object: any},
  setAccountingEntry: Dispatch<SetStateAction<{value: string, valid: boolean, object: any}>>,
  setAmount?: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setAmountDebit?: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setAmountCredit?: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setTreasuryAccount: Dispatch<SetStateAction<{value: string, valid: boolean, object: any}>>,
  setPaymentMethod: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setDate: Dispatch<SetStateAction<{value: string, valid: boolean, starting: boolean}>>
};

const AppInputAccountingEntry: React.FunctionComponent<AppInputAccountingEntryProps> = ({component, solid, companyForUser, accountingAccountsForCompany, treasuryAccountsForCompany, invoice, accountingEntry, setAccountingEntry, setAmount, setAmountCredit, setAmountDebit, setTreasuryAccount, setPaymentMethod, setDate}) => {
  const {lang} = StateLanguage()

  const handleChangeAccountingEntry = (item: AccountingEntry) => {
    setAccountingEntry({value: item.id, valid: true, object: item})
    setTreasuryAccount({value: '', valid: false, object: null})
    setPaymentMethod({value: '', valid: false})
    setDate({value: '', valid: false, starting: true})

    legendValid(`${prefixLegend(component)}legend-accounting-entry-required`)
    legendValidInvalidRestart(`${prefixLegend(component)}legend-treasury-account-required`)
    legendValidInvalidRestart(`${prefixLegend(component)}legend-payment-method-required`)
    legendValidInvalidRestart(`${prefixLegend(component)}legend-date-required`)

    if (setAmount) {
      setAmount({value: '', valid: false})

      legendValidInvalidIconRestart(`${prefixLegend(component)}input-amount`, `${prefixLegend(component)}legend-amount-valid`)
      legendValidInvalidIconRestart(`${prefixLegend(component)}input-amount`, `${prefixLegend(component)}legend-amount-required`)
    }

    if (invoice && setAmountDebit && setAmountCredit) {
      setAmountDebit({value: '', valid: false})
      setAmountCredit({value: (item.balance >= invoice.amount.debt) ? invoice.amount.debt.toString() : item.balance.toString(), valid: true})

      legendValidInvalidIconRestart(`${prefixLegend(component)}input-amount-debit`, `${prefixLegend(component)}legend-amount-debit-valid`)
      legendValidInvalidIconRestart(`${prefixLegend(component)}input-amount-debit`, `${prefixLegend(component)}legend-amount-debit-required`)
      legendValidIcon(`${prefixLegend(component)}input-amount-credit`, `${prefixLegend(component)}legend-amount-credit-valid`)
      legendValidIcon(`${prefixLegend(component)}input-amount-credit`, `${prefixLegend(component)}legend-amount-credit-required`)
    }
  }

  function accountingIdentifiedEntries(): AccountingEntry[] {
    return accountingAccountsForCompany.filter((item) => (item.id_contact.length > 0))
  }

  function accountingUnidentifiedEntries(): AccountingEntry[] {
    return accountingAccountsForCompany.filter((item) => (item.id_contact.length === 0))
  }

  return (
    <>
      <Dropdown>
        <Dropdown.Toggle variant="select2 select2-container select2-container--bootstrap5 select2-container--below select2-container--focus select2-container--open w-100 p-0">
          <span className="selection">
            <span className={`select2-selection select2-selection--single form-select ${solid && "form-select-solid"}`}>
              <span className={solid ? "select2-selection__rendered" : ""} role="textbox">
                {accountingEntry.valid ? accountingEntryFormat(lang, treasuryAccountsForCompany, companyForUser.currency, accountingEntry.object) : lang.labels.selectOption}
              </span>
            </span>
          </span>
        </Dropdown.Toggle>
        <Dropdown.Menu variant="select2-container select2-container--bootstrap5 select2-container--open w-100">
          <span className="select2-dropdown select2-dropdown--below">
            <span className="select2-results">
              <ul className="select2-results__options" role="listbox">
                <li className={`select2-results__option select2-results__option--selectable ${accountingAccountsForCompany.length > 0 && "d-none"}`}>
                  <Dropdown.Item bsPrefix="select2-results__option__text">{lang.labels.selectOption}</Dropdown.Item>
                </li>
                { invoice && accountingIdentifiedEntries().length > 0 &&
                  <>
                    <span className={`select2-results__option select2-results__option--group ${accountingUnidentifiedEntries().length === 0 && "d-none"}`}>
                      <Dropdown.Header bsPrefix="select2-results__option text-dark fw-bold">{lang.labels.identifiedTransactions}</Dropdown.Header>
                    </span>
                    <ul className="select2-results__options mh-100px mb-2" role="listbox">
                      { accountingIdentifiedEntries().map (( (item, index) => { return (
                        <li key={index} className={`select2-results__option select2-results__option--selectable ${item.id === accountingEntry.value && "select2-results__option--selected"}`} role="option" aria-selected={item.id === accountingEntry.value}>
                          <Dropdown.Item bsPrefix="select2-results__option__text" onClick={() => handleChangeAccountingEntry(item)}>{accountingEntryFormat(lang, treasuryAccountsForCompany, companyForUser.currency, item)}</Dropdown.Item>
                        </li>
                      )}))}
                    </ul>
                  </>
                }
                { accountingUnidentifiedEntries().length > 0 &&
                  <>
                    { invoice &&
                      <span className={`select2-results__option select2-results__option--group ${accountingIdentifiedEntries().length === 0 && "d-none"}`}>
                        <Dropdown.Header bsPrefix="select2-results__option text-dark fw-bold">{lang.labels.unidentifiedTransactions}</Dropdown.Header>
                      </span>
                    }
                    <ul className="select2-results__options mh-100px" role="listbox">
                      { accountingUnidentifiedEntries().map (( (item, index) => { return (
                        <li key={index} className={`select2-results__option select2-results__option--selectable ${item.id === accountingEntry.value && "select2-results__option--selected"}`} role="option" aria-selected={item.id === accountingEntry.value}>
                          <Dropdown.Item bsPrefix="select2-results__option__text" onClick={() => handleChangeAccountingEntry(item)}>{accountingEntryFormat(lang, treasuryAccountsForCompany, companyForUser.currency, item)}</Dropdown.Item>
                        </li>
                      )}))}
                    </ul>
                  </>
                }
              </ul>
            </span>
          </span>
        </Dropdown.Menu>
      </Dropdown>
      { !invoice && !setAmountDebit && !setAmountCredit &&
        <div className="form-text text-justify">{lang.labels.unidentifiedTransactionToRegisterInContact}</div>
      }
      <AppLegend component={component} attribute={{check: accountingEntry.valid, name: "accounting-entry", index: null, sub_index: null}} container={{valid: false, required: true, identical: false, exists: false}}></AppLegend>
    </>
  )
}

export default AppInputAccountingEntry;
